import { cn, StoredPreference } from "@design-system/utils";
import React, {
  Children,
  isValidElement,
  useCallback,
  useMemo,
  useState,
} from "react";
import { z } from "zod";

import { FiltersAction } from "./data-table-filters-action";
import { FiltersClearAll } from "./data-table-filters-clear-all";
import {
  FiltersContext,
  type RegisteredField,
} from "./data-table-filters-context";
import { FiltersField } from "./data-table-filters-field";
import { FiltersManager } from "./data-table-filters-manager";
import { FiltersSearch } from "./data-table-filters-search";

const VisibilityPreferenceSchema = z.object({
  visibleFieldIds: z.array(z.string()),
});

/**
 * Whether a child is the row's action slot. Matched on the component identity the
 * consumer wrote, so `.Action` may sit anywhere among the children — but an
 * `.Action` wrapped in another component is invisible to this and lands in the
 * control zone, where it would wrap.
 */
function isActionElement(child: React.ReactNode): boolean {
  return isValidElement(child) && child.type === FiltersAction;
}

interface DataTableFiltersProps {
  /** The field ids visible before the reader customizes anything. */
  defaultVisibleFieldIds: string[];
  /** Per-surface `localStorage` key for field visibility (adr/0007). */
  storageKey?: string;
  className?: string;
  children: React.ReactNode;
}

/**
 * @deprecated Use `FilterBar` with `FiltersProvider` from `@design-system/filters`.
 * This row stays until the listings built on it are migrated.
 */
function DataTableFilters({
  defaultVisibleFieldIds,
  storageKey,
  className,
  children,
}: DataTableFiltersProps) {
  const preference = useMemo(
    () =>
      storageKey
        ? new StoredPreference(storageKey, VisibilityPreferenceSchema)
        : undefined,
    [storageKey]
  );

  const [fields, setFields] = useState<RegisteredField[]>([]);
  const [visibleFieldIds, setVisibleFieldIds] = useState(() => {
    const defaults = { visibleFieldIds: defaultVisibleFieldIds };
    return (preference?.read(defaults) ?? defaults).visibleFieldIds;
  });

  // Registration keeps the consumer's declared order: fields mount in document
  // order, and a re-registration replaces in place rather than appending.
  const registerField = useCallback((field: RegisteredField) => {
    setFields((current) => {
      const index = current.findIndex((entry) => entry.id === field.id);
      if (index === -1) return [...current, field];
      const next = [...current];
      next[index] = field;
      return next;
    });
    return () =>
      setFields((current) => current.filter((entry) => entry.id !== field.id));
  }, []);

  const persistVisibility = useCallback(
    (next: string[]) => {
      setVisibleFieldIds(next);
      preference?.write({ visibleFieldIds: next });
    },
    [preference]
  );

  const showField = useCallback(
    (id: string) =>
      persistVisibility(
        visibleFieldIds.includes(id)
          ? visibleFieldIds
          : [...visibleFieldIds, id]
      ),
    [persistVisibility, visibleFieldIds]
  );

  const hideField = useCallback(
    (id: string) =>
      persistVisibility(visibleFieldIds.filter((entry) => entry !== id)),
    [persistVisibility, visibleFieldIds]
  );

  const value = useMemo(
    () => ({
      state: { fields, visibleFieldIds },
      actions: { registerField, showField, hideField },
    }),
    [fields, visibleFieldIds, registerField, showField, hideField]
  );

  // Two zones, not one wrapping row: in a single row the action is just another
  // flex item, so it wraps onto a second line as soon as the controls fill the
  // first. Split by CHILD TYPE rather than by position — the consumer owns the
  // order, so the root cannot assume the action comes last.
  const childList = Children.toArray(children);
  const actionChildren = childList.filter(isActionElement);
  const controlChildren = childList.filter((child) => !isActionElement(child));

  return (
    <FiltersContext value={value}>
      <div
        data-testid="filters-row"
        // `items-start`, so the action stays level with the first row of controls
        // rather than drifting down the middle once they wrap.
        className={cn("flex items-start gap-2", className)}
      >
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          {controlChildren}
        </div>
        {actionChildren.length > 0 && (
          <div className="flex shrink-0 items-center gap-2">
            {actionChildren}
          </div>
        )}
      </div>
    </FiltersContext>
  );
}

DataTableFilters.Search = FiltersSearch;
DataTableFilters.Field = FiltersField;
DataTableFilters.Manager = FiltersManager;
DataTableFilters.ClearAll = FiltersClearAll;
DataTableFilters.Action = FiltersAction;

export { DataTableFilters };
export type { DataTableFiltersProps };
export type { DataTableFiltersActionProps } from "./data-table-filters-action";
export type { DataTableFiltersClearAllProps } from "./data-table-filters-clear-all";
export type { DataTableFiltersFieldProps } from "./data-table-filters-field";
export type { DataTableFiltersManagerProps } from "./data-table-filters-manager";
export type { DataTableFiltersSearchProps } from "./data-table-filters-search";
